import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/shared/react';
import onboardingCoinsImage from './assets/onboarding-coins.png';
import onboardingWalletImage from './assets/onboarding-wallet.png';

const SLIDES: {
  image: string;
  alt: string;
  subtitle: MessageDescriptor;
  title: MessageDescriptor;
  description: MessageDescriptor;
}[] = [
  {
    image: onboardingCoinsImage,
    alt: 'Coins and Markets',
    subtitle: msg`MARKET`,
    title: msg`Wide Range of Coins and Markets`,
    description: msg`Access top-performing coins across multiple market pairs.`,
  },
  {
    image: onboardingWalletImage,
    alt: 'Wallet',
    subtitle: msg`PORTFOLIO`,
    title: msg`Secure Built-In Wallet`,
    description: msg`Your assets are stored safely in a dedicated cold wallet, fully protected.`,
  },
];

// temporary change for standalone app
// {
//   image: onboardingTradeImage,
//   alt: 'Trading Tools',
//   subtitle: msg`TRADING`,
//   title: msg`Access Advanced Trading Tools`,
//   description: msg`Access modern features like stop-loss & take-profit orders, profit/loss reporting.`,
// },

const AUTO_ADVANCE_INTERVAL = 10000;
const SWIPE_THRESHOLD = 50;

export const OnboardingSlider: FC = () => {
  const { t } = useLingui();
  const [currentStep, setCurrentStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const startX = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPaused || isDragging) return;

    const timer = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % SLIDES.length);
    }, AUTO_ADVANCE_INTERVAL);

    return () => clearInterval(timer);
  }, [isPaused, isDragging]);

  useEffect(() => {
    if (isPaused && !isDragging) {
      const timer = setTimeout(() => setIsPaused(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isPaused, isDragging]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsPaused(true);
    setIsDragging(true);
    startX.current = e.touches[0].clientX;
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const diff = startX.current - e.touches[0].clientX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset > 0 && currentStep < SLIDES.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else if (dragOffset < 0 && currentStep > 0) {
        setCurrentStep((prev) => prev - 1);
      }
    }

    setDragOffset(0);
    setIsPaused(false);
  };

  const goToStep = (step: number) => {
    setIsPaused(true);
    setCurrentStep(step);
  };

  const dragPercent = containerRef.current
    ? (dragOffset / containerRef.current.offsetWidth) * 100
    : 0;
  const translateX = -(currentStep * 100) - (isDragging ? dragPercent : 0);

  const slide = SLIDES[currentStep];

  return (
    <div className="w-full">
      <div className="overflow-hidden">
        <div
          ref={containerRef}
          className="w-full"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex w-full"
            style={{
              transform: `translateX(${translateX}%)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            {SLIDES.map((s) => (
              <div
                key={s.alt}
                className="w-full flex-shrink-0 flex items-center justify-center"
              >
                <img
                  src={s.image}
                  alt={s.alt}
                  className="w-full h-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {SLIDES.length > 1 && (
        <div className="flex gap-2 justify-center my-6">
          {SLIDES.map((s, i) => (
            <button
              key={s.alt}
              type="button"
              onClick={() => goToStep(i)}
              className={cn(
                'size-3 rounded-full transition-all duration-300',
                currentStep === i
                  ? 'bg-background-brand'
                  : 'bg-background-brand/25',
              )}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>
      )}

      <div className="text-center px-4">
        <p className="text-sm uppercase tracking-wider text-content-tertiary mb-1">
          {t(slide.subtitle)}
        </p>
        <h1 className="text-xl font-bold text-content-primary mb-3 leading-7">
          {t(slide.title)}
        </h1>
        <p className="text-base text-content-secondary">
          {t(slide.description)}
        </p>
      </div>
    </div>
  );
};
